import axios from 'axios'
import { camelcaseKeys, pascalcaseKeys } from '../util/keys'
import { getAccessToken } from '../util/webStorageUtils'
import { logException } from '../util/errorUtils'
import { cacheAdapterEnhancer } from 'axios-extensions'

const FOODSBY_BASE_URL = process.env.REACT_APP_FOODSBY_BASE_URL || ''
const API_MONO = '/api-monolith'

export const callApi = {
  get: (endpoint, body, config) => baseApi(endpoint, 'get', body, config),
  post: (endpoint, body, config) => baseApi(endpoint, 'post', body, config),
  patch: (endpoint, body, config) => baseApi(endpoint, 'patch', body, config),
  delete: (endpoint, body, config) => baseApi(endpoint, 'delete', body, config),
  put: (endpoint, body, config) => baseApi(endpoint, 'put', body, config)
}

const api = axios.create({
  headers: { 'Cache-Control': 'no-cache' },
  adapter: cacheAdapterEnhancer(axios.defaults.adapter, {
    enabledByDefault: false,
    cacheFlag: 'cache'
  })
})

const camelize = res => {
  if (
    res &&
    res.data &&
    !res.data.access_token &&
    typeof res.data === 'object'
  ) {
    try {
      res.data = camelcaseKeys(res.data, { deep: true })
    } catch (ex) {
      logException(ex)
    }
  }
  return res
}

const baseApi = async (endpoint, method, body, configOpts = {}) => {
  let config = {
    baseURL: FOODSBY_BASE_URL,
    method: method,
    url: endpoint,
    data: body,
    withCredentials: true,
    ...configOpts
  }
  // pull token from local storage
  const token = getAccessToken()
  if (token) {
    config['headers'] = {
      Authorization: `Bearer ${token}`
    }
  }
  try {
    return camelize(await api(config))
  } catch (ex) {
    ex.response = camelize(ex.response)
    throw ex
  }
}

// api services
export const searchNearbyLocations = (address, coords) => {
  return callApi.get(
    `${API_MONO}/location/search?street=${encodeURIComponent(
      address.street
    )}&city=${encodeURIComponent(address.city)}&state=${encodeURIComponent(
      address.state
    )}&zip=${encodeURIComponent(address.zip)}&latitude=${encodeURIComponent(
      coords.lat
    )}&longitude=${encodeURIComponent(coords.lng)}`
  )
}

export const deliveringRestaurants = locationId =>
  callApi.get(`${API_MONO}/location/${locationId}/delivering-restaurants`)

export const patchThankYouModal = message =>
  callApi.patch(`${API_MONO}/user/mobile-popup`, pascalcaseKeys(message))

export const loadLocationById = locationId =>
  callApi.get(`${API_MONO}/location/${locationId}`, null, { cache: true })

export const loadDeliveriesForLocation = (locationId, date) =>
  callApi.get(
    `${API_MONO}/location/${locationId}/schedule?day=${date}&duration=1`
  )

export const loadDeliveryDropoffById = deliveryDropoffId =>
  callApi.get(`${API_MONO}/deliveries/dropoff/${deliveryDropoffId}`, null, {
    cache: true
  })

export const loadPastOrdersByStoreId = storeId =>
  callApi.get(`${API_MONO}/store/${storeId}/past-orders`)

export const loadMenuByStoreId = storeId =>
  callApi.get(`${API_MONO}/store/${storeId}/menu`, null, { cache: true })

export const loadMenuItem = menuItemId =>
  callApi.get(`${API_MONO}/menu/item/${menuItemId}`, null, { cache: true })

export const loadOrderCart = dropoffId =>
  callApi.get(`${API_MONO}/v2/cart/dropoff/${dropoffId}`).catch(error => {
    if (error.message === 'Request failed with status code 404') {
      return {} // Makes sure the promise is resolved, so the chain continues
    }
    throw error // Otherwise, rethrow to keep the Promise rejected
  })

export const loadCheckoutCart = dropoffId =>
  callApi.get(`${API_MONO}/cart/dropoff/${dropoffId}/full`)

export const addItemsToCart = (dropoffId, menuItems) =>
  callApi.post(
    `${API_MONO}/v2/cart/dropoff/${dropoffId}`,
    pascalcaseKeys(menuItems)
  )

export const removeItemFromCart = (dropoffId, menuItem) =>
  callApi.delete(
    `${API_MONO}/cart/dropoff/${dropoffId}`,
    pascalcaseKeys(menuItem)
  )

export const submitCoupon = (dropoffId, couponCode) =>
  callApi.post(
    `${API_MONO}/cart/dropoff/${dropoffId}/coupon`,
    pascalcaseKeys({
      couponCode
    })
  )

export const submitCredit = (dropoffId, creditAmount) =>
  callApi.post(
    `${API_MONO}/cart/dropoff/${dropoffId}/credit`,
    pascalcaseKeys({
      creditAmount
    })
  )

export const loadAvailableCredits = () =>
  callApi.get(`/api/v1/wallets/balances`)

export const loadCreditCards = () => callApi.get(`${API_MONO}/payments`)

export const checkoutOrder = checkoutData =>
  callApi.post(`${API_MONO}/checkout`, pascalcaseKeys(checkoutData))

export const loadOrderHistory = (page = 1, pageSize = 5) =>
  callApi.get(`${API_MONO}/order/history?page=${page}&pageSize=${pageSize}`)

export const attemptResetPassword = forgotPasswordFormData =>
  callApi.post(`/api/v1/uaa/passwords/forgot`, forgotPasswordFormData)

export const attemptCompleteResetPassword = completeResetPasswordFormData =>
  callApi.post(`/api/v1/uaa/passwords/reset`, completeResetPasswordFormData)

export const attemptChangePassword = changePasswordFormData =>
  callApi.put(`/api/v1/uaa/passwords/current`, changePasswordFormData)

export const reorderPastOrder = (dropoffId, pastOrderId) =>
  callApi.post(
    `${API_MONO}/deliveries/dropoff/${dropoffId}/reorder/${pastOrderId}`
  )

export const loadOrderByDate = (date, deliveryLocationId) =>
  callApi.get(
    `${API_MONO}/order/day?day=${date}&locationId=${deliveryLocationId}`
  )

export const loadOrderById = orderId =>
  callApi.get(`${API_MONO}/order/${orderId}/detail`)

// export const loadNotificationPreferences = () =>
//   callApi.get(`/api/v1/switchboard/preferences`)
// export const updateNotificationPreference = notificationPreference =>
//   callApi.patch(`/api/v1/switchboard/preferences`, notificationPreference)

export const loadPaymentOptions = () => callApi.get(`${API_MONO}/payments`)
export const deletePaymentOption = ccProfileId =>
  callApi.delete(`${API_MONO}/payments/${ccProfileId}`)

export const loadCreditHistory = () =>
  callApi.get(`${API_MONO}/user/credits/history`)

export const submitBuilding = submitBuildingFormData =>
  callApi.post(
    `${API_MONO}/search/requestfoodsby`,
    pascalcaseKeys(submitBuildingFormData)
  )

export const unsubscribeUser = emailAddress =>
  callApi.post(`${API_MONO}/mail/unsubscribe`, pascalcaseKeys({ emailAddress }))
